import React, { useState } from 'react';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, CheckCircle, XCircle, Trash2, Image, Film, Music, Sparkles, Plus, Loader2, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyWallpapers, WallpapersResponse, updateWallpapers } from '@/lib/collections/wallpapers';
import { Address } from '@/lib/db-client';
import { UserUploadModal } from '@/components/UserUploadModal';

type WallpaperWithStatus = WallpapersResponse;

interface MyUploadsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userAddress: string;
  showUploadModal: boolean;
  onOpenUploadModal: () => void;
  onCloseUploadModal: () => void;
}

function StatusBadge({ status }: { status?: string }) {
  if (status === 'published') {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', borderRadius: 20,
        background: 'rgba(34,197,94,0.15)',
        border: '1px solid rgba(34,197,94,0.3)',
        color: '#4ade80',
        fontSize: 10, fontWeight: 700,
        fontFamily: "'Syne', sans-serif",
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        <CheckCircle size={10} />
        Published
      </div>
    );
  }
  if (status === 'declined') {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', borderRadius: 20,
        background: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.3)',
        color: '#f87171',
        fontSize: 10, fontWeight: 700,
        fontFamily: "'Syne', sans-serif",
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        <XCircle size={10} />
        Declined
      </div>
    );
  }
  // pending (default)
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 20,
      background: 'rgba(234,179,8,0.12)',
      border: '1px solid rgba(234,179,8,0.3)',
      color: '#facc15',
      fontSize: 10, fontWeight: 700,
      fontFamily: "'Syne', sans-serif",
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      <Clock size={10} />
      Pending Review
    </div>
  );
}

function MediaIcon({ mediaType }: { mediaType?: string }) {
  if (mediaType === 'live-wallpaper') return <Film size={22} color="#a78bfa" strokeWidth={1.5} />;
  if (mediaType === 'ringtone') return <Music size={22} color="#a78bfa" strokeWidth={1.5} />;
  if (mediaType === 'icon') return <Sparkles size={22} color="#a78bfa" strokeWidth={1.5} />;
  return <Image size={22} color="#a78bfa" strokeWidth={1.5} />;
}

const UploadItem: React.FC<{
  wallpaper: WallpaperWithStatus;
}> = ({ wallpaper }) => {
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const previewUrl = wallpaper.thumbnailUrl || wallpaper.imageUrl;
  const canShowPreview = !!previewUrl && !imgFailed && wallpaper.mediaType !== 'ringtone';
  const status = wallpaper.status;
  const isHidden = wallpaper.hidden === true;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const ok = await updateWallpapers(wallpaper.id, {
        status: 'deleted',
        uploadedBy: Address.publicKey(wallpaper.uploadedBy),
        createdAt: wallpaper.createdAt,
        price: wallpaper.price,
        downloadCount: wallpaper.downloadCount,
        title: wallpaper.title,
        imageUrl: wallpaper.imageUrl,
        thumbnailUrl: wallpaper.thumbnailUrl,
        category: wallpaper.category,
        mediaType: wallpaper.mediaType,
        hidden: wallpaper.hidden,
        reviewedAt: wallpaper.reviewedAt,
        reviewedBy: wallpaper.reviewedBy ? Address.publicKey(wallpaper.reviewedBy) : undefined,
        rejectionReason: wallpaper.rejectionReason,
        categorySetAt: wallpaper.categorySetAt,
      });
      if (!ok) {
        toast.error('Could not remove this upload. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: isHidden ? 0.5 : 1, y: 0 }}
      exit={{ opacity: 0, x: -20, scale: 0.96 }}
      style={{
        borderRadius: 12,
        background: confirming ? 'rgba(239,68,68,0.06)' : isHidden ? 'rgba(255,255,255,0.015)' : 'rgba(255,255,255,0.03)',
        border: 'none',
        overflow: 'hidden',
        transition: 'background 0.18s ease, opacity 0.18s ease',
        flexShrink: 0,
      }}
    >
      {/* Main row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '6px 12px',
      }}>
        {/* Thumbnail — compact portrait */}
        <div style={{
          width: 44, height: 56, borderRadius: 8,
          flexShrink: 0,
          background: '#08070f',
          border: '1px solid rgba(139,92,246,0.22)',
          overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {canShowPreview ? (
            wallpaper.mediaType === 'live-wallpaper' ? (
              <video
                src={previewUrl}
                muted
                playsInline
                autoPlay
                loop
                preload="auto"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={() => setImgFailed(true)}
              />
            ) : (
              <img
                src={previewUrl}
                alt={wallpaper.title}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={() => setImgFailed(true)}
              />
            )
          ) : (
            <MediaIcon mediaType={wallpaper.mediaType} />
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: isHidden ? 'rgba(240,240,245,0.5)' : '#f0f0f5',
            fontFamily: "'Syne', sans-serif",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}>
            {wallpaper.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <StatusBadge status={status} />
            {isHidden && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 20,
                background: 'rgba(156,163,175,0.12)',
                border: '1px solid rgba(156,163,175,0.25)',
                color: '#9ca3af',
                fontSize: 10, fontWeight: 700,
                fontFamily: "'Syne', sans-serif",
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                <EyeOff size={9} />
                Hidden
              </div>
            )}
          </div>
        </div>

        {/* Delete button — deletes the upload directly */}
        {!confirming && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setConfirming(true)}
            disabled={deleting}
            title="Remove upload"
            aria-label="Remove upload"
            style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              color: '#f87171',
              opacity: deleting ? 0.5 : 1,
              transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
            }}
          >
            <Trash2 size={13} />
          </motion.button>
        )}
      </div>

      {/* Inline confirm row */}
      <AnimatePresence>
        {confirming && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              padding: '0 12px 12px',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{
                borderTop: '1px solid rgba(239,68,68,0.15)',
                paddingTop: 10,
              }}>
                <p style={{
                  fontSize: 12, color: 'rgba(240,240,245,0.55)',
                  margin: '0 0 10px',
                  fontFamily: "'Syne', sans-serif",
                  lineHeight: 1.4,
                }}>
                  This will remove the upload from your catalog and the public store. Anyone who already purchased it keeps access in their library.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleDelete}
                    disabled={deleting}
                    style={{
                      flex: 1, padding: '7px 12px',
                      borderRadius: 8,
                      background: deleting ? 'rgba(239,68,68,0.4)' : 'rgba(239,68,68,0.85)',
                      border: '1px solid rgba(239,68,68,0.5)',
                      color: '#fff', fontSize: 12, fontWeight: 700,
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontFamily: "'Syne', sans-serif",
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    {deleting ? (
                      <>
                        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        Removing…
                      </>
                    ) : (
                      <>
                        <Trash2 size={12} />
                        Remove
                      </>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setConfirming(false)}
                    disabled={deleting}
                    style={{
                      flex: 1, padding: '7px 12px',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(240,240,245,0.65)', fontSize: 12, fontWeight: 600,
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontFamily: "'Syne', sans-serif",
                    }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const MyUploadsPanel: React.FC<MyUploadsPanelProps> = ({ isOpen, onClose, userAddress, showUploadModal, onOpenUploadModal, onCloseUploadModal }) => {
  // Lock body scroll while panel is open
  useBodyScrollLock(isOpen);

  const { data: allWallpapers } = useRealtimeData<WallpaperWithStatus[]>(
    subscribeManyWallpapers,
    isOpen && !!userAddress
  );

  const myUploads = (allWallpapers ?? []).filter(
    w => w.uploadedBy?.toLowerCase() === userAddress?.toLowerCase() && w.status !== 'deleted'
  ).sort((a, b) => (b.createdAt ?? b.tarobase_created_at ?? 0) - (a.createdAt ?? a.tarobase_created_at ?? 0));

  const visibleCount = myUploads.filter(w => !w.hidden).length;
  const hiddenCount = myUploads.filter(w => w.hidden).length;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              style={{
                position: 'fixed', inset: 0, zIndex: 4500,
                background: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(10px)',
              }}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', stiffness: 350, damping: 32 }}
              style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 'min(420px, 100vw)',
                zIndex: 4501,
                background: '#0c0b18',
                borderLeft: '1px solid rgba(167,139,250,0.15)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 20px 9px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <div>
                  <h3 style={{
                    fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: 0,
                    fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em',
                  }}>
                    My Uploads
                  </h3>
                  <p style={{
                    fontSize: 12, color: 'rgba(167,139,250,0.6)', margin: '2px 0 0',
                    fontFamily: "'DM Mono', monospace",
                  }}>
                    {myUploads.length} item{myUploads.length !== 1 ? 's' : ''}
                    {hiddenCount > 0 && (
                      <span style={{ color: 'rgba(156,163,175,0.6)', marginLeft: 6 }}>
                        · {hiddenCount} hidden
                      </span>
                    )}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* Upload button */}
                  <motion.button
                    whileHover={{ scale: 1.06, boxShadow: '0 0 14px rgba(139,92,246,0.4)' }}
                    whileTap={{ scale: 0.94 }}
                    onClick={() => onOpenUploadModal()}
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: 'rgba(139,92,246,0.2)',
                      border: '1px solid rgba(167,139,250,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: '#a78bfa',
                    }}
                  >
                    <Plus size={15} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: 'rgba(240,240,245,0.5)',
                    }}
                  >
                    <X size={15} />
                  </motion.button>
                </div>
              </div>

              {/* Content */}
              <div
                className="hide-scrollbar"
                style={{
                  flex: 1, overflowY: 'auto',
                  padding: 16,
                  display: 'flex', flexDirection: 'column', gap: 8,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; width: 0; height: 0; }`}</style>
                {myUploads.length === 0 ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    flex: 1, gap: 12, paddingTop: 60,
                  }}>
                    <div style={{
                      width: 60, height: 60, borderRadius: '50%',
                      background: 'rgba(70,245,224,0.1)',
                      border: '1px solid rgba(70,245,224,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Image size={26} color="#46f5e0" strokeWidth={1.5} />
                    </div>
                    <p style={{
                      fontSize: 14, fontWeight: 700, color: 'rgba(240,240,245,0.5)',
                      margin: 0, fontFamily: "'Syne', sans-serif", textAlign: 'center',
                    }}>
                      No uploads yet
                    </p>
                    <p style={{
                      fontSize: 12, color: 'rgba(240,240,245,0.3)',
                      margin: 0, fontFamily: "'DM Mono', monospace", textAlign: 'center',
                    }}>
                      Tap + to submit your first piece
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.04, boxShadow: '0 0 16px rgba(139,92,246,0.3)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => onOpenUploadModal()}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '10px 20px', borderRadius: 10,
                        background: 'rgba(139,92,246,0.2)',
                        border: '1px solid rgba(167,139,250,0.35)',
                        color: '#a78bfa', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        marginTop: 4,
                      }}
                    >
                      <Plus size={14} />
                      Upload Content
                    </motion.button>
                  </div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {myUploads.map(w => (
                      <UploadItem key={w.id} wallpaper={w} />
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <UserUploadModal
        isOpen={showUploadModal}
        onClose={onCloseUploadModal}
      />
    </>
  );
};
